import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Bell, ExternalLink, UserRound, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/common/Modal';
import { useAppSelector } from '@/hooks';
import { usePlatformId } from '@/hooks/usePlatformId';
import { RootState } from '@/store';
import {
  useGetSuspiciousIpAlertsQuery,
  useGetSuspiciousIpUnreadCountQuery,
  useReviewSuspiciousIpAlertMutation,
} from '@/store/api/suspiciousIpApi';
import { formatReadableDateTime, showToastMessage } from '@/utils';

const ALERT_SOUND_SRC = '/sound/notification_sound.mp3';

const createAlertAudio = () => {
  const audio = new Audio(ALERT_SOUND_SRC);
  audio.preload = 'auto';
  return audio;
};

const playAlertSound = async (audio: HTMLAudioElement | null) => {
  if (!audio) return;

  audio.muted = false;
  audio.volume = 1;
  audio.currentTime = 0;
  await audio.play();
};

export function SuspiciousIpAlerts() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const alertAudioRef = useRef<HTMLAudioElement | null>(null);
  const previousUnreadRef = useRef<number | null>(null);
  const isOwner = useAppSelector(
    (state: RootState) => state.user.data?.role?.name?.toUpperCase() === 'OWNER'
  );
  const availableRoles = useAppSelector((state: RootState) => state.roles.availableRoles);
  const platformId = usePlatformId();
  const ownerId =
    availableRoles.find((role) => role.name.toUpperCase() === 'WHITELABEL')?.id ??
    availableRoles.find((role) => role.name.toUpperCase() === 'OWNER')?.id;

  const { data: unreadData } = useGetSuspiciousIpUnreadCountQuery(
    platformId ? { platformId } : {},
    {
      pollingInterval: 5000,
      skip: !isOwner,
    }
  );
  const { data, isLoading } = useGetSuspiciousIpAlertsQuery(
    { reviewed: false, ...(platformId ? { platformId } : {}) },
    { skip: !open || !isOwner, pollingInterval: 5000 }
  );
  const [reviewAlert, { isLoading: isReviewing }] = useReviewSuspiciousIpAlertMutation();

  const alerts = data?.alerts ?? [];
  const unread = Number(unreadData?.count ?? 0);

  const getAlertUserLabel = (alert: (typeof alerts)[number]) => {
    const username = alert.user.username?.trim();
    const mobile = String(alert.user.mobile ?? alert.user.mobileNumber ?? alert.user.phoneNo ?? '').trim();
    return username || mobile || '--';
  };

  useEffect(() => {
    if (!isOwner) return undefined;

    const unlockAudio = () => {
      if (!alertAudioRef.current) {
        alertAudioRef.current = createAlertAudio();
      }

      const audio = alertAudioRef.current;
      if (!audio) return;

      audio.muted = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
        })
        .catch(() => {
          audio.muted = false;
        });
    };

    window.addEventListener('pointerdown', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, [isOwner]);

  useEffect(() => {
    if (!isOwner || !unreadData) return;

    const previousUnread = previousUnreadRef.current;
    previousUnreadRef.current = unread;

    if (previousUnread === null || unread <= previousUnread) return;

    if (!alertAudioRef.current) {
      alertAudioRef.current = createAlertAudio();
    }

    playAlertSound(alertAudioRef.current).catch(() => undefined);
  }, [isOwner, unread, unreadData]);

  const openTarget = async (id: string | number, path: string) => {
    try {
      await reviewAlert(id).unwrap();
    } catch {
      showToastMessage('error', 'Unable to mark alert as reviewed');
    }
    setOpen(false);
    navigate(path);
  };

  if (!isOwner) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Suspicious IP notifications"
        onClick={() => setOpen(true)}
        className="relative rounded-full border border-green3 bg-card p-2 text-card-foreground transition hover:bg-muted"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Suspicious IP Alerts"
        closeBtn={<X className="h-5 w-5" />}
        style="md:w-[720px] md:max-w-3xl"
      >
        <div className="space-y-3 pt-4">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {unread} unread alert{unread === 1 ? '' : 's'}
            </span>
            <Button
              size="sm"
              className="h-8 rounded-md bg-blue px-3 py-0.5 text-white hover:bg-blue/90"
              onClick={() => {
                setOpen(false);
                navigate('/user-ips');
              }}
            >
              View all
            </Button>
          </div>

          {isLoading && <p className="p-6 text-center text-sm text-muted-foreground">Loading alerts...</p>}
          {!isLoading && alerts.length === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">No suspicious activity alerts.</p>
          )}

          {alerts.map((alert) => (
            <div
              key={String(alert.id)}
              className="rounded-lg border border-green3 bg-muted/40 p-3 shadow-sm"
            >
              <div className="flex gap-3">
                <span className="mt-0.5 self-start rounded-full border border-green3 bg-card p-2 text-green3">
                  <AlertTriangle className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">
                    Suspicious IP Login{' '}
                    <span className="text-green3">- {getAlertUserLabel(alert)}</span>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    IP: <span className="font-mono">{alert.ip}</span> |{' '}
                    {formatReadableDateTime(alert.loginAt)}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {ownerId && (
                      <Button
                        size="sm"
                        className="rounded-md bg-green3 text-black hover:bg-green3/90"
                        disabled={isReviewing}
                        onClick={() =>
                          openTarget(
                            alert.id,
                            `/members/${ownerId}/sub/${alert.user.id}/${alert.path}/account-summary`
                          )
                        }
                      >
                        <UserRound className="mr-1 h-3.5 w-3.5" />
                        View User
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-green3 bg-card text-card-foreground hover:bg-muted"
                      disabled={isReviewing}
                      onClick={() => openTarget(alert.id, `/user-ips?ip=${alert.ip}`)}
                    >
                      <ExternalLink className="mr-1 h-3.5 w-3.5" />
                      View IP
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
}

export default SuspiciousIpAlerts;
